import React, { useState, useEffect, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { 
  Calendar, Clock, CheckCircle2, XCircle, Trash2, ExternalLink, 
  Search, RefreshCw, Layers, Sparkles, Filter, Mail, Phone, Loader2, AlertCircle
} from 'lucide-react';
import styles from './DemoRequests.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const DemoRequests = () => {
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  const fetchDemos = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/demos`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (response.ok && data.demos) {
        setDemos(data.demos);
      } else {
        toast.error(data.message || 'Failed to fetch demo requests');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to connect to backend server');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDemos();
  }, [fetchDemos]);

  const handleStatusUpdate = async (id, status) => {
    const token = localStorage.getItem('token');
    setProcessingId(id);

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/demos/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(`Demo request marked as ${status}`);
        setDemos((prev) =>
          prev.map((d) => (d._id === id ? { ...d, status, bookingUrl: data.demo?.bookingUrl || d.bookingUrl } : d))
        );
      } else {
        toast.error(data.message || 'Failed to update status');
      }
    } catch (err) {
      console.error(err);
      toast.error('Server error updating status');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this demo request?')) return;

    const token = localStorage.getItem('token');
    setProcessingId(id);

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/demos/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success('Demo request removed');
        setDemos((prev) => prev.filter((d) => d._id !== id));
      } else {
        const data = await response.json();
        toast.error(data.message || 'Failed to delete demo request');
      }
    } catch (err) {
      console.error(err);
      toast.error('Server error deleting demo request');
    } finally {
      setProcessingId(null);
    }
  };

  // Metrics calculations
  const stats = useMemo(() => {
    const total = demos.length;
    const pending = demos.filter((d) => d.status === 'pending').length;
    const accepted = demos.filter((d) => d.status === 'accepted').length;
    const rejected = demos.filter((d) => d.status === 'rejected').length;
    return { total, pending, accepted, rejected };
  }, [demos]);

  // Filter & Search matching
  const filteredDemos = useMemo(() => {
    return demos.filter((demo) => {
      const matchesSearch =
        demo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        demo.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        demo.phone.includes(searchTerm);

      const matchesStatus = activeFilter === 'all' || demo.status === activeFilter;

      return matchesSearch && matchesStatus;
    });
  }, [demos, searchTerm, activeFilter]);

  const getInitials = (name) => {
    if (!name) return 'D';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  const renderStatusBadge = (status) => {
    switch (status) {
      case 'accepted':
        return (
          <span className={`${styles.statusBadge} ${styles.statusAccepted}`}>
            <CheckCircle2 size={13} /> Accepted
          </span>
        );
      case 'rejected':
        return (
          <span className={`${styles.statusBadge} ${styles.statusRejected}`}>
            <XCircle size={13} /> Rejected
          </span>
        );
      default:
        return (
          <span className={`${styles.statusBadge} ${styles.statusPending}`}>
            <Clock size={13} /> Pending
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <Loader2 size={32} className="animate-spin" color="#3b82f6" />
          <h3>Loading Demo Requests...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.headerSection}>
        <div className={styles.titleGroup}>
          <h1>
            <Sparkles color="#3b82f6" size={28} /> Demo Management Hub
          </h1>
          <p>Review, schedule, and manage prospective client demo bookings for Provaantech.</p>
        </div>
      </div>

      {/* Analytics Stats Grid */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIconWrapper} style={{ background: '#eff6ff', color: '#2563eb' }}>
            <Layers size={26} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statLabel}>Total Requests</span>
            <span className={styles.statValue}>{stats.total}</span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIconWrapper} style={{ background: '#fffbeb', color: '#d97706' }}>
            <Clock size={26} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statLabel}>Pending Approval</span>
            <span className={styles.statValue}>{stats.pending}</span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIconWrapper} style={{ background: '#f0fdf4', color: '#16a34a' }}>
            <CheckCircle2 size={26} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statLabel}>Accepted Demos</span>
            <span className={styles.statValue}>{stats.accepted}</span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIconWrapper} style={{ background: '#fef2f2', color: '#dc2626' }}>
            <XCircle size={26} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statLabel}>Rejected</span>
            <span className={styles.statValue}>{stats.rejected}</span>
          </div>
        </div>
      </div>

      {/* Toolbar Controls */}
      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <Search size={18} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.filterGroup}>
          <button
            className={`${styles.filterBtn} ${activeFilter === 'all' ? styles.filterBtnActive : ''}`}
            onClick={() => setActiveFilter('all')}
          >
            All ({stats.total})
          </button>
          <button
            className={`${styles.filterBtn} ${activeFilter === 'pending' ? styles.filterBtnActive : ''}`}
            onClick={() => setActiveFilter('pending')}
          >
            Pending ({stats.pending})
          </button>
          <button
            className={`${styles.filterBtn} ${activeFilter === 'accepted' ? styles.filterBtnActive : ''}`}
            onClick={() => setActiveFilter('accepted')}
          >
            Accepted ({stats.accepted})
          </button>
          <button
            className={`${styles.filterBtn} ${activeFilter === 'rejected' ? styles.filterBtnActive : ''}`}
            onClick={() => setActiveFilter('rejected')}
          >
            Rejected ({stats.rejected})
          </button>
        </div>

        <button onClick={fetchDemos} className={styles.refreshBtn} title="Refresh Data">
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {/* Table Section */}
      {filteredDemos.length === 0 ? (
        <div className={styles.tableContainer}>
          <div className={styles.emptyState}>
            <AlertCircle size={40} color="#94a3b8" />
            <h3>No Demo Requests Found</h3>
            <p>Try adjusting your search query or filter selection.</p>
          </div>
        </div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Requester</th>
                <th>Contact Info</th>
                <th>Requested Date & Time</th>
                <th>Business Needs</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDemos.map((demo) => {
                const dateObj = new Date(demo.date);
                const formattedDate = dateObj.toLocaleDateString("en-US", {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                });
                const formattedTime = dateObj.toLocaleTimeString("en-US", {
                  hour: '2-digit',
                  minute: '2-digit'
                });

                return (
                  <tr key={demo._id}>
                    <td>
                      <div className={styles.userInfoCell}>
                        <div className={styles.avatarCircle}>
                          {getInitials(demo.name)}
                        </div>
                        <div>
                          <div className={styles.userName}>{demo.name}</div>
                          <div className={styles.userSub}>Requested {new Date(demo.createdAt || demo.date).toLocaleDateString()}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Mail size={13} color="#64748b" /> {demo.email}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#64748b' }}>
                          <Phone size={13} color="#64748b" /> {demo.phone}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className={styles.dateCell}>
                        <span className={styles.dateMain}>{formattedDate}</span>
                        <span className={styles.timeSub}>
                          <Clock size={12} color="#3b82f6" /> {formattedTime}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div style={{ maxWidth: '240px', wordBreak: 'break-word', color: '#475569' }}>
                        {demo.message || <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>No notes provided</span>}
                      </div>
                    </td>
                    <td>
                      <div>
                        {renderStatusBadge(demo.status)}
                        {demo.bookingUrl && (
                          <div>
                            <a
                              href={demo.bookingUrl}
                              target="_blank"
                              rel="noreferrer"
                              className={styles.bookingUrlBtn}
                            >
                              Cal.com Booking <ExternalLink size={12} />
                            </a>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className={styles.actionBtns}>
                        {demo.status !== 'accepted' && (
                          <button
                            className={styles.btnAccept}
                            onClick={() => handleStatusUpdate(demo._id, 'accepted')}
                            disabled={processingId === demo._id}
                          >
                            {processingId === demo._id ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <>
                                <CheckCircle2 size={14} /> Accept
                              </>
                            )}
                          </button>
                        )}

                        {demo.status !== 'rejected' && (
                          <button
                            className={styles.btnReject}
                            onClick={() => handleStatusUpdate(demo._id, 'rejected')}
                            disabled={processingId === demo._id}
                          >
                            {processingId === demo._id ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <>
                                <XCircle size={14} /> Reject
                              </>
                            )}
                          </button>
                        )}

                        <button
                          className={styles.btnDelete}
                          onClick={() => handleDelete(demo._id)}
                          disabled={processingId === demo._id}
                          title="Delete Request"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DemoRequests;
